import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

type TooltipContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement>;
};

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

// Provider kept for API compatibility; currently just renders children.
const TooltipProvider = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

const Tooltip = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement>(null);

  return (
    <TooltipContext.Provider value={{ open, setOpen, triggerRef }}>
      {children}
    </TooltipContext.Provider>
  );
};

type TooltipTriggerProps = React.HTMLAttributes<HTMLElement> & {
  asChild?: boolean;
};

const TooltipTrigger = React.forwardRef<HTMLElement, TooltipTriggerProps>(
  ({ asChild = false, onMouseEnter, onMouseLeave, onFocus, onBlur, ...props }, ref) => {
    const context = React.useContext(TooltipContext);
    const Comp: any = asChild ? Slot : "button";

    const mergedRef = React.useCallback(
      (node: HTMLElement) => {
        if (context?.triggerRef) {
          (context.triggerRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      },
      [context, ref]
    );

    if (!context) {
      return <Comp ref={ref} {...props} />;
    }

    return (
      <Comp
        ref={mergedRef}
        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
          context.setOpen(true);
          onMouseEnter?.(event);
        }}
        onMouseLeave={(event: React.MouseEvent<HTMLElement>) => {
          context.setOpen(false);
          onMouseLeave?.(event);
        }}
        onFocus={(event: React.FocusEvent<HTMLElement>) => {
          context.setOpen(true);
          onFocus?.(event);
        }}
        onBlur={(event: React.FocusEvent<HTMLElement>) => {
          context.setOpen(false);
          onBlur?.(event);
        }}
        {...props}
      />
    );
  },
);
TooltipTrigger.displayName = "TooltipTrigger";

type TooltipContentProps = React.HTMLAttributes<HTMLDivElement> & {
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
};

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ className, side = "top", align = "center", ...props }, ref) => {
    const context = React.useContext(TooltipContext);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    
    // Respect a passed `hidden` prop (used in the sidebar when expanded).
    const isHidden = (props as any).hidden;

    React.useEffect(() => {
      if (!context?.open || !context.triggerRef.current) return;

      const updatePosition = () => {
        const trigger = context.triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();

        let top = 0;
        let left = 0;

        switch (side) {
          case "right":
            top = rect.top + rect.height / 2;
            left = rect.right + 12;
            break;
          case "left":
            top = rect.top + rect.height / 2;
            left = rect.left - 12;
            break;
          case "bottom":
            top = rect.bottom + 12;
            left = rect.left + rect.width / 2;
            break;
          case "top":
          default:
            top = rect.top - 12;
            left = rect.left + rect.width / 2;
            break;
        }

        setPosition({ top, left });
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    }, [context?.open, context?.triggerRef, side]);

    if (!context || !context.open || isHidden) {
      return null;
    }

    const content = (
      <div
        ref={ref}
        role="tooltip"
        data-side={side}
        data-align={align}
        style={{
          position: "fixed",
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: side === "right" || side === "left" 
            ? "translateY(-50%)" 
            : "translateX(-50%)",
        }}
        className={cn(
          "z-[9999] overflow-hidden rounded-lg border border-border/50 bg-gradient-to-br from-primary/95 to-primary/90 backdrop-blur-sm px-3 py-2 text-xs font-medium text-primary-foreground shadow-lg shadow-primary/20",
          "animate-in fade-in-0 zoom-in-95 duration-200",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        {...props}
      />
    );

    return createPortal(content, document.body);
  },
);
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
